/**
 * AdminPresentielPanel — Liste des demandes de séances présentiel (Coaching Hybride/VIP)
 * ET des forfaits présentiels achetés via la page Tarifs (Personal Training, EMS, Duo,
 * Scan 3D...). Ces forfaits ne touchent JAMAIS aux comptes/accès de l'app.
 */
import React, { useEffect, useState } from 'react';
import { Loader2, MapPin, Check, Trash2, Clock, CheckCheck, ShoppingBag, CreditCard, Landmark } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_LBL = { pending: 'En attente', confirmed: 'Confirmée', done: 'Terminée' };
const STATUS_COLOR = {
  pending: 'bg-amber-500/15 text-amber-300',
  confirmed: 'bg-emerald-500/15 text-emerald-300',
  done: 'bg-zinc-500/15 text-zinc-400',
};

const PurchasesSection = ({ fmtDate }) => {
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/admin/presentiel-purchases`, { credentials: 'include' });
        if (r.ok) setPurchases((await r.json()).items || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="bg-zinc-900 border border-white/10 rounded-2xl overflow-hidden overflow-x-auto mb-6" data-testid="admin-presentiel-purchases-panel">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10">
        <ShoppingBag className="w-4 h-4 text-primary" />
        <h3 className="font-bold text-white text-sm">Forfaits présentiels achetés (Personal Training, EMS, Duo, Scan 3D...)</h3>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-10"><Loader2 className="w-6 h-6 text-primary animate-spin" /></div>
      ) : (
        <table className="w-full text-sm" data-testid="admin-presentiel-purchases-table">
          <thead className="bg-zinc-800/70 text-zinc-400">
            <tr>
              {['Acheté le', 'Client', 'Forfait', 'Montant', 'Paiement', 'Statut'].map((h) => (
                <th key={h} className="text-left px-4 py-3 font-semibold whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {purchases.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-zinc-500" data-testid="admin-presentiel-purchases-empty">Aucun forfait présentiel acheté pour le moment.</td></tr>
            )}
            {purchases.map((p) => (
              <tr key={`${p.source}-${p.id}`} className="border-t border-white/5 text-zinc-300" data-testid={`presentiel-purchase-row-${p.id}`}>
                <td className="px-4 py-3 whitespace-nowrap text-zinc-500">{fmtDate(p.created_at)}</td>
                <td className="px-4 py-3">
                  <div className="text-white font-medium">{p.customer_name || '-'}</div>
                  <div className="text-xs text-zinc-500">{p.customer_email}</div>
                </td>
                <td className="px-4 py-3 max-w-[220px] truncate" title={p.description}>{p.description || '-'}</td>
                <td className="px-4 py-3 whitespace-nowrap font-semibold text-white">{p.amount ? `${p.amount} CHF` : '-'}</td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className="inline-flex items-center gap-1.5 text-xs text-zinc-400">
                    {p.source === 'stripe' ? <CreditCard className="w-3.5 h-3.5 text-primary" /> : <Landmark className="w-3.5 h-3.5 text-primary" />}
                    {p.payment_method}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full ${p.status === 'paid' ? 'bg-emerald-500/15 text-emerald-300' : 'bg-amber-500/15 text-amber-300'}`}>
                    {p.status === 'paid' ? 'Payé' : 'En attente'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export const AdminPresentielPanel = ({ csrfToken, fmtDate }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const fetchWithCsrf = (url, options = {}) => fetch(url, {
    ...options,
    credentials: 'include',
    headers: { ...options.headers, 'X-CSRF-Token': csrfToken },
  });

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/presentiel-requests`, { credentials: 'include' });
      if (r.ok) setItems((await r.json()).requests || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    setBusyId(id);
    try {
      const r = await fetchWithCsrf(`${API}/api/admin/presentiel-requests/${id}/status`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status }),
      });
      if (r.ok) setItems((prev) => prev.map((b) => (b.booking_id === id ? { ...b, status } : b)));
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer définitivement cette demande ?')) return;
    setBusyId(id);
    try {
      const r = await fetchWithCsrf(`${API}/api/admin/presentiel-requests/${id}`, { method: 'DELETE' });
      if (r.ok) setItems((prev) => prev.filter((b) => b.booking_id !== id));
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>;
  }

  return (
    <div data-testid="admin-presentiel-panel">
      <PurchasesSection fmtDate={fmtDate} />
      <div className="bg-zinc-900 border border-white/10 rounded-2xl overflow-hidden overflow-x-auto">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10">
        <MapPin className="w-4 h-4 text-primary" />
        <h3 className="font-bold text-white text-sm">Demandes de séances présentiel (studio Nyon)</h3>
      </div>
      <table className="w-full text-sm" data-testid="admin-presentiel-table">
        <thead className="bg-zinc-800/70 text-zinc-400">
          <tr>
            {['Demandée le', 'Client', 'Date souhaitée', 'Note', 'Statut', 'Actions'].map((h) => (
              <th key={h} className="text-left px-4 py-3 font-semibold whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && (
            <tr><td colSpan={6} className="px-4 py-8 text-center text-zinc-500" data-testid="admin-presentiel-empty">Aucune demande de séance présentiel.</td></tr>
          )}
          {items.map((b) => (
            <tr key={b.booking_id} className="border-t border-white/5 text-zinc-300" data-testid={`presentiel-row-${b.booking_id}`}>
              <td className="px-4 py-3 whitespace-nowrap text-zinc-500">{fmtDate(b.created_at)}</td>
              <td className="px-4 py-3">
                <div className="text-white font-medium">{b.name || '-'}</div>
                <div className="text-xs text-zinc-500">{b.email}</div>
              </td>
              <td className="px-4 py-3 whitespace-nowrap">
                <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-primary" />{b.preferred_date}{b.preferred_time ? ` · ${b.preferred_time}` : ''}</span>
              </td>
              <td className="px-4 py-3 max-w-[220px] truncate" title={b.note}>{b.note || '-'}</td>
              <td className="px-4 py-3">
                <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full ${STATUS_COLOR[b.status] || 'bg-zinc-700 text-zinc-300'}`} data-testid={`presentiel-status-${b.booking_id}`}>
                  {STATUS_LBL[b.status] || b.status}
                </span>
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-1.5">
                  {b.status !== 'confirmed' && (
                    <button onClick={() => setStatus(b.booking_id, 'confirmed')} disabled={busyId === b.booking_id}
                      className="p-1.5 text-zinc-500 hover:text-emerald-400 transition-colors disabled:opacity-40" title="Confirmer" data-testid={`presentiel-confirm-${b.booking_id}`}>
                      <Check className="w-4 h-4" />
                    </button>
                  )}
                  {b.status !== 'done' && (
                    <button onClick={() => setStatus(b.booking_id, 'done')} disabled={busyId === b.booking_id}
                      className="p-1.5 text-zinc-500 hover:text-zinc-300 transition-colors disabled:opacity-40" title="Marquer terminée" data-testid={`presentiel-done-${b.booking_id}`}>
                      <CheckCheck className="w-4 h-4" />
                    </button>
                  )}
                  <button onClick={() => remove(b.booking_id)} disabled={busyId === b.booking_id}
                    className="p-1.5 text-zinc-500 hover:text-red-400 transition-colors disabled:opacity-40" title="Supprimer" data-testid={`presentiel-delete-${b.booking_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
};

export default AdminPresentielPanel;
